import { computed } from '@angular/core';
import {
  type DiagramPayload,
  type ElementValidation,
  type GenericObject,
  type GroupedElementValidation,
  type MenuLocationWithCallback,
  type MetamodelMetadata,
  type ModelValidationCurrentState,
  type ModelerConfig,
  type ModelingAssistanceCurrentState,
  SupportedMetamodels,
  type UUID,
  type VisibilityIndicator,
  type ViewportChange,
  type VisibilityIndicators,
} from '@bizzdesign/modeler/models';
import {
  getElementType,
  isElementRelation,
  isElementReusedFromRepo,
  isEmptyTextBlock,
} from '@bizzdesign/modeler/utils-modeler';
import { getElementIcon } from '@bizzdesign/modeler/utils-toolbar';
import type { ContentItem, ExternalData } from '@bizzdesign/shared/models';
import {
  patchState,
  signalMethod,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  distinctUntilChanged,
  map,
  pipe,
  startWith,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { getGroupedValidations } from './modeler.store.utils';

export type FlagConfiguration = {
  reset?: boolean;
};

type ModelerState = {
  viewId: string; // ID of published view or view frame is published as
  viewName: string; // name of published view or view frame is published as
  viewDescription: string;
  baselineContent: DiagramPayload | undefined;
  content: DiagramPayload | undefined;
  config: ModelerConfig | undefined;
  validation: {
    data: ElementValidation[];
    groupedData: GroupedElementValidation;
    selectedElementId: string;
    selectedCategory: VisibilityIndicator['kind'] | undefined;
    // Incremented to replay MA category opens when the requested category does not change.
    selectedCategoryRequestId: number;
    hasErrors: boolean;
    visibilityIndicators: VisibilityIndicators;
  };
  metamodelMetadata: MetamodelMetadata;
  modelingAssistanceCurrentState: ModelingAssistanceCurrentState | undefined;
  modelValidationCurrentState: ModelValidationCurrentState | undefined;
  isViewMode: boolean; // when editor is initialized in read-only mode (ex: viewing published views)
  isHandMode: boolean; // when the hand is turned on/view mode when modelling
  selectedElement: GenericObject | undefined;
  selectedElements: GenericObject[];
  isZoomOrPanInProgress: boolean;
  viewport: ViewportChange;
  mouseLocationWithCallback: MenuLocationWithCallback;
  selectedSideToolbarButtonId: string;
  lastPublishedView: { id: UUID; name: string } | null;
};

const visibilityIndicatorsInitState: VisibilityIndicators = {
  conflicts: 'on',
  unrecognizedElements: 'on',
  newElements: 'off',
};
const initialState: ModelerState = {
  viewId: '',
  viewName: '',
  viewDescription: '',
  content: undefined,
  baselineContent: undefined,
  config: undefined,
  validation: {
    data: [],
    groupedData: {},
    selectedElementId: '',
    selectedCategory: undefined,
    selectedCategoryRequestId: 0,
    hasErrors: false,
    visibilityIndicators: visibilityIndicatorsInitState,
  },
  metamodelMetadata: {
    currentMetamodelName: SupportedMetamodels.FREEFORMAT,
    targetMetamodelName: undefined,
  },
  modelingAssistanceCurrentState: undefined,
  modelValidationCurrentState: undefined,
  isViewMode: false,
  isHandMode: false,
  selectedElement: undefined,
  selectedElements: [],
  isZoomOrPanInProgress: false,
  viewport: {
    x: 0,
    y: 0,
    zoom: 0,
  },
  mouseLocationWithCallback: {
    location: { x: -999, y: -999 },
    callback: () => {},
  },
  selectedSideToolbarButtonId: '',
  lastPublishedView: null,
};

export const ModelerLocalStore = signalStore(
  { protectedState: false },
  withState(initialState),
  withComputed((store) => {
    return {
      isReusedRelation: computed(
        () =>
          isElementRelation(store.selectedElement()) &&
          isElementReusedFromRepo(store.selectedElement()),
      ),
      selectedContentItem: computed<ContentItem | undefined>(() => {
        const selectedEl = store.selectedElement();

        if (!selectedEl) return;

        const elementType = getElementType(selectedEl);
        const elementIcon = getElementIcon(selectedEl);

        const id =
          elementType === 'fromRepo' || elementType === 'relation'
            ? selectedEl['promotion']?.['semanticsId'] ||
              selectedEl['semanticsId']
            : selectedEl['id'];

        const typeName = selectedEl['promotion']?.['metamodelName']
          ? `${selectedEl['promotion']?.['metamodelName']}.${selectedEl['promotion']?.['typeName']}`
          : selectedEl['metaModelTerm'].replace(':', '.');

        const externalData: ExternalData | undefined =
          selectedEl['metadata']?.['external'];

        const isDeleted =
          selectedEl['metadata']?.['isDeletedFromRepo'] ?? false;

        return {
          id,
          scope: 'object',
          typeName,
          icon: elementIcon,
          name: selectedEl['name']?.['en'] ?? '',
          metaModel: store.metamodelMetadata().currentMetamodelName,
          isRepo: elementType === 'fromRepo',
          isBasic: elementType === 'shape' || elementType === 'formalObject',
          isLink: elementType === 'line' || elementType === 'relation',
          externalId: externalData?.id,
          externalSource: externalData?.source,
          isDeleted,
          graphicId: selectedEl['id'],
        };
      }),
      isEmptyTextBlock: computed(() =>
        isEmptyTextBlock(store.selectedElement()),
      ),
    };
  }),
  withMethods((store) => {
    const setValidationData = rxMethod<ElementValidation[]>(
      pipe(
        tap((data) => {
          const hasErrors = data.some((item) => item.status === 'error');
          const groupedData = getGroupedValidations(data);

          patchState(store, {
            validation: {
              ...store.validation(),
              data: [...data],
              hasErrors,
              groupedData: groupedData,
            },
          });
        }),
      ),
    );

    const refreshValidationListAfterGraphicSelection = (
      graphicId: string,
      isLocked: boolean,
    ) => {
      const dataWithUpdatedLockedElement = store
        .validation()
        .data.map((val) =>
          val.id === graphicId
            ? { ...val, isGraphicLockedByPeer: isLocked }
            : val,
        );

      setValidationData(dataWithUpdatedLockedElement);
    };

    const setSelectedElementId = (selectedElementId: string) => {
      patchState(store, {
        validation: {
          ...store.validation(),
          selectedElementId,
        },
      });
    };

    const setSelectedValidationCategory = (
      selectedCategory: VisibilityIndicator['kind'] | undefined,
    ) => {
      const validation = store.validation();

      patchState(store, {
        validation: {
          ...validation,
          selectedCategory,
          selectedCategoryRequestId: validation.selectedCategoryRequestId + 1,
        },
      });
    };

    const toggleVisibilityIndicators = (
      visibilityIndicator: keyof VisibilityIndicators,
    ) => {
      const value =
        store.validation.visibilityIndicators[visibilityIndicator]();
      const newValue = value === 'on' ? 'off' : 'on';
      patchState(store, {
        validation: {
          ...store.validation(),
          visibilityIndicators: {
            ...store.validation.visibilityIndicators(),
            [visibilityIndicator]: newValue,
          },
        },
      });
    };

    const resetVisibilityIndicators = () => {
      patchState(store, {
        validation: {
          ...store.validation(),
          visibilityIndicators: visibilityIndicatorsInitState,
        },
      });
    };

    const setMetamodelMetadata = (data?: MetamodelMetadata) => {
      patchState(store, {
        metamodelMetadata: data ? { ...data } : initialState.metamodelMetadata,
      });
    };

    const setModelingAssistanceCurrentState = (
      data?: ModelingAssistanceCurrentState,
    ) => {
      patchState(store, {
        modelingAssistanceCurrentState: data ? { ...data } : undefined,
      });
    };

    const setModelValidationCurrentState = (
      data?: ModelValidationCurrentState,
    ) => {
      patchState(store, {
        modelValidationCurrentState: data ? { ...data } : undefined,
      });
    };

    const setViewModeFlag = signalMethod<boolean>((isViewMode) => {
      patchState(store, {
        isViewMode,
      });
    });

    const updateHandMode = (config?: FlagConfiguration) => {
      patchState(store, {
        isHandMode: config?.reset ? false : !store.isHandMode(),
      });
    };

    const updateSelectedElement = (
      selectedElement: GenericObject | undefined,
      selection?: GenericObject[],
    ) => {
      const selectedElementId = selectedElement?.['id'];
      const selectedElements = [
        ...(selection ?? (selectedElement ? [selectedElement] : [])),
      ];

      patchState(store, {
        selectedElement,
        selectedElements,
        validation: {
          ...store.validation(),
          selectedElementId,
        },
      });
    };

    const updateViewport = rxMethod<ViewportChange>(
      pipe(
        switchMap((viewport) =>
          timer(250).pipe(
            map(() => ({ viewport, inProgress: false })),
            startWith({ viewport, inProgress: true }),
          ),
        ),
        distinctUntilChanged((a, b) => a.inProgress === b.inProgress),
        tap((e) => {
          patchState(store, {
            viewport: e.viewport,
            isZoomOrPanInProgress: e.inProgress,
          });
        }),
      ),
    );

    const setMousePositionWithCallback = rxMethod<MenuLocationWithCallback>(
      pipe(
        tap((mouseLocationWithCallback) => {
          patchState(store, {
            mouseLocationWithCallback,
          });
        }),
      ),
    );

    const setSelectedSideToolbarButtonId = (btnId: string) => {
      patchState(store, { selectedSideToolbarButtonId: btnId });
    };

    const setViewId = (viewId: string): void => {
      patchState(store, { viewId });
    };

    const setViewName = (viewName: string): void => {
      patchState(store, { viewName });
    };

    const setViewDescription = (viewDescription: string): void => {
      patchState(store, { viewDescription });
    };

    const setConfig = (config?: ModelerConfig): void => {
      patchState(store, { config });
    };

    const setBaselineContent = (baselineContent: DiagramPayload): void => {
      patchState(store, { baselineContent });
    };

    const setContent = (content: DiagramPayload): void => {
      patchState(store, { content });
    };

    const setLastPublishedView = (
      lastPublishedView: { id: UUID; name: string } | null,
    ): void => {
      patchState(store, { lastPublishedView });
    };

    return {
      setViewId,
      setViewName,
      setViewDescription,
      setConfig,
      setContent,
      setBaselineContent,
      setLastPublishedView,
      setValidationData,
      refreshValidationListAfterGraphicSelection,
      setSelectedElementId,
      setSelectedValidationCategory,
      toggleVisibilityIndicators,
      resetVisibilityIndicators,
      setMetamodelMetadata,
      setModelingAssistanceCurrentState,
      setModelValidationCurrentState,
      setViewModeFlag,
      updateHandMode,
      updateSelectedElement,
      updateViewport,
      setMousePositionWithCallback,
      setSelectedSideToolbarButtonId,
    };
  }),
);

export type ModelerLocalStore = InstanceType<typeof ModelerLocalStore>;
